import React, { useContext, useEffect, useState } from 'react';
import Cookies from 'js-cookie';

import { getClosestLocation } from 'utils/geometryUtils';
import ClosestCurrentConditions from './components/ClosestCurrentConditions';
import MapStuff from './components/MapStuff';
import { temperatureColorScale } from 'utils/colorUtils';
import WidgetLoadingAnimation from 'components/Widgets/WidgetLoadingAnimation';
import { ErrorMessage } from 'components/Widgets/ErrorMessage';
import { useAPIData } from 'hooks/useAPIData';
import { ColorContext, SetUniqueWidgetRefreshContext, UniqueWidgetRefreshContext } from 'App';
import WidgetTimeUpdate from 'components/Widgets/WidgetTimeUpdate';
import useDataRefresh from 'hooks/useDataRefresh';
import { getAirportConditionsbyState } from 'services/weather/airports.service';

let dataCache = null;

export default function MapForecast({
  fullScreen = false,
  widgetRefresh,
  setWidgetRefresh,
  dimensions,
  preview = false,
  sidebarOpen,
  containerSizePx,
}) {
  const [fetching, setFetching] = useState(true);
  const [data, setData] = useState([]);
  const [zoom, setZoom] = useState(6);
  const [center, setCenter] = useState(null);
  const [closestLocation, setClosestLocation] = useState(null);
  const [error, setError] = useState({ state: false, message: '' });
  const widgetWidth = Number.isFinite(containerSizePx?.width) ? containerSizePx.width : null;
  const isTallWidget = dimensions?.[0] == 1 && dimensions?.[1] == 2;
  const isWideWidget = dimensions?.[0] == 2 && dimensions?.[1] == 1;
  const useCompactCurrentConditionsLayout = !fullScreen && (isTallWidget || isWideWidget);
  const currentConditionsPanelWidthHint = isWideWidget
    ? Math.max(220, Math.round((widgetWidth || 720) * 0.44))
    : Math.max(220, Math.round((widgetWidth || 360) * 0.94));
  const compactMapOverlay = !fullScreen && (dimensions?.[0] !== 1 || dimensions?.[1] !== 1);

  const ROUTE = 'getAirportConditionsbyState';

  const FusionData = useAPIData({
    successCallback: () => {
      setFetching(false);
    },
    errorCallback: () => {
      setError({ state: true, message: 'Error fetching data' });
      setFetching(false);
    },
    fetchingCallback: () => {
      setFetching(true);
    },
  });

  const uwf = useContext(UniqueWidgetRefreshContext);
  const LOCATION = Cookies.get('city') + '-' + Cookies.get('state');
  const UNIQUEKEY = LOCATION + '-' + ROUTE;
  // const [oldRouteData, setOldRouteData] = useState(uwf[ROUTE]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await FusionData.forceGet({
          key: ROUTE,
          apiFunction: getAirportConditionsbyState,
        });
        dataCache = res.data.result;
        setData(dataCache);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setFetching(false);
      }
    };

    fetchData();
  }, [uwf[UNIQUEKEY]]);

  const dataRefresh = useDataRefresh();

  useEffect(() => {
    setCenter([Cookies.get('latitude'), Cookies.get('longitude')]);

    (async () => {
      try {
        if (!dataCache || dataCache === null) {
          const res = await FusionData.get({
            key: 'getAirportConditionsbyState',
            apiFunction: getAirportConditionsbyState,
          });
          dataCache = res.data.result;
          setData(dataCache);

          setFetching(false);
        } else {
          setData(dataCache);
          setFetching(false);
        }
      } catch (err) {
        setError({ state: true, message: err.message });
      }
    })();
  }, [dataRefresh.widgetRefresh]);

  useEffect(() => {
    if (!center) return;

    const closestLoc = getClosestLocation(data, center);
    setClosestLocation(closestLoc);
  }, [data, center]);

  if (error.state && !fetching) {
    return <ErrorMessage message={error.message} />;
  }

  if (fetching && !fullScreen) {
    return <WidgetLoadingAnimation widgetName={'MapForecast'} dimensions={dimensions} />;
  }
  if (fetching) return <WidgetLoadingAnimation />;

  const GradientScaleBar = ({ compact = false }) => {
    const labelFontSize = compact ? '0.9rem' : '1rem';
    const labelMargin = compact ? 2 : 5;
    const barHeight = compact ? '0.8rem' : '1rem';

    return (
      <>
        <div
          style={{
            width: '100%',
            height: barHeight,
            borderRadius: 10,
            background: 'rgb(64,23,39)',
            background:
              'linear-gradient(90deg, rgba(64,23,39,1) 5%, rgba(237,1,113,1) 16%, rgba(164,5,158,1) 25%, rgba(94,10,169,1) 34%, rgba(43,12,154,1) 44%, rgba(16,131,198,1) 55%, rgba(56,191,55,1) 68%, rgba(220,222,0,1) 82%, rgba(255,91,0,1) 95%)',
          }}
        ></div>
        <div className="d-flex justify-content-between align-items-center flex-row w-100">
          {[-20, 34, 106].map((temp, index) => (
            <div
              key={index}
              style={{
                margin: labelMargin,
                borderRadius: 5,
                fontSize: labelFontSize,
                lineHeight: 1.1,
              }}
            >
              {temp}
            </div>
          ))}
        </div>
      </>
    );
  };

  if (!data) return <WidgetLoadingAnimation />;

  if (data && data.length === 0) {
    return (
      <div>
        <div style={{ padding: 15 }}>No data found.</div>
      </div>
    );
  }
  if (fullScreen) {
    if (data && data.length) {
      return (
        <div className="h-100 w-100 d-flex flex-column">
          <WidgetTimeUpdate
            updateIntervalBasis={60}
            route={'getAirportConditionsbyState'}
            addMargin
          />
          {center && (
            <MapStuff
              sidebarOpen={sidebarOpen}
              data={data}
              center={center}
              zoom={zoom}
              fullScreen={fullScreen}
              setZoom={setZoom}
            />
          )}
          <div className="mt-2 mb-0 pb-0 pt-1 w-100 d-flex flex-column justify-content-center align-items-center">
            <div className="fs-5 mb-2 text-white justify-content-center align-self-center mx-auto text-center">
              Current Temperatures {data.length && data[0].utc_time}
            </div>
            <div className="bg-black bg-gradient"></div>

            <div
              className="d-none d-sm-flex flex-column justify-content-center align-items-center"
              style={{
                width: '90%',
              }}
            >
              <div
                className="w-100 border-2 rounded-2"
                style={{
                  height: '1rem',
                  background: 'rgb(64,23,39)',
                  background:
                    'linear-gradient(90deg, rgba(64,23,39,1) 5%, rgba(237,1,113,1) 16%, rgba(164,5,158,1) 25%, rgba(94,10,169,1) 34%, rgba(43,12,154,1) 44%, rgba(16,131,198,1) 55%, rgba(56,191,55,1) 68%, rgba(220,222,0,1) 82%, rgba(255,91,0,1) 95%)',
                }}
              ></div>
              <div className="w-100 d-flex justify-content-between align-items-center flex-wrap text-truncate">
                {[-20, -5, 8, 16, 26, 48, 70, 88, 106].map((temp, index) => (
                  <div key={index} className="m-1">
                    {temp}
                  </div>
                ))}
              </div>
            </div>
            <div className="d-flex d-sm-none flex-column" style={{ width: '90%' }}>
              <GradientScaleBar />
            </div>
          </div>
        </div>
      );
    }
  }
  //1 wide, 2 tall
  if ((dimensions?.[1] || 0) >= 2) {
    if (data && data.length && closestLocation) {
      return (
        <div className="d-flex h-100 flex-column overflow-hidden">
          <WidgetTimeUpdate updateIntervalBasis={60} route={'getAirportConditionsbyState'} />
          <MapStuff
            sidebarOpen={sidebarOpen}
            data={data}
            center={center}
            zoom={zoom}
            setZoom={setZoom}
            preview={preview}
            compactTooltip={compactMapOverlay}
          />
          <div className="m-1 mt-3 h-50 flex-grow-1 overflow-auto">
            <GradientScaleBar compact={useCompactCurrentConditionsLayout} />
            <ClosestCurrentConditions
              closestLocation={closestLocation}
              compact={useCompactCurrentConditionsLayout}
              panelWidthHintPx={currentConditionsPanelWidthHint}
            />
          </div>
        </div>
      );
    }
  }

  //1 wide, 1 tall
  if (dimensions[0] == 1 && dimensions[1] == 1) {
    if (data && data.length) {
      return (
        <div className="d-flex flex-column h-100">
          <WidgetTimeUpdate updateIntervalBasis={60} route={'getAirportConditionsbyState'} />
          <MapStuff
            data={data}
            sidebarOpen={sidebarOpen}
            center={center}
            zoom={zoom}
            setZoom={setZoom}
            preview={preview}
          />
        </div>
      );
    }
  }

  //2 wide, 1 tall
  if ((dimensions?.[0] || 0) >= 2 && dimensions?.[1] == 1) {
    if (data && data.length && closestLocation) {
      return (
        <div className="d-flex h-100 flex-column">
          <WidgetTimeUpdate updateIntervalBasis={60} route={'getAirportConditionsbyState'} />
          <div className="d-flex h-100 flex-row overflow-hidden">
            <div className="mx-auto me-3 w-50 flex-grow-1 overflow-auto">
              <ClosestCurrentConditions
                closestLocation={closestLocation}
                compact={useCompactCurrentConditionsLayout}
                panelWidthHintPx={currentConditionsPanelWidthHint}
              />
              <GradientScaleBar compact={useCompactCurrentConditionsLayout} />
            </div>
            <MapStuff
              sidebarOpen={sidebarOpen}
              data={data}
              center={center}
              zoom={zoom}
              setZoom={setZoom}
              preview={preview}
              compactTooltip={compactMapOverlay}
            />
          </div>
        </div>
      );
    }
  }
}
